import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setToken, removeToken, getToken, isTokenExpired, setRefreshToken, removeRefreshToken, getRefreshToken } from "../../utils/auth";
import { loginApi, logoutApi } from "../../services/authService";

// 从 token 中提取用户信息的辅助函数
const decodeToken = (token: string): User => {
  try {
    const decoded = JSON.parse(atob(token.split(".")[1]));
    return {
      id: decoded.sub || "1",
      name: decoded.name || "User",
      email: decoded.email || "",
      role: decoded.role || "",
    };
  } catch (e) {
    return {
      id: "1",
      name: "User",
      email: "",
      role: "",
    };
  }
};

// 从本地 token 恢复用户登录状态
export const setUserFromToken = createAsyncThunk(
  "auth/setUserFromToken",
  async (token: string) => {
    setToken(token); // 可选：更新 token 到 localStorage
    const user = decodeToken(token);
    return { token, user };
  },
);

// 登录接口
export const login = createAsyncThunk(
  "auth/login",
  async (
    credentials: { username: string; password: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await loginApi(credentials);
      // Store both tokens
      setToken(response.token);
      setRefreshToken(response.refreshToken);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Login failed");
    }
  },
);

// 登出逻辑
export const logout = createAsyncThunk("auth/logout", async () => {
  try {
    // Call the logout API
    await logoutApi();
  } catch (error) {
    // Even if the API call fails, we still want to remove the tokens
    console.error('Logout API call failed:', error);
  } finally {
    // Remove both tokens
    removeToken();
    removeRefreshToken();
  }
  return null;
});

// 类型定义
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// 修改点：在初始化时检测 localStorage 中的 token
const storedToken = getToken();
const initialUser = storedToken && !isTokenExpired(storedToken) ? decodeToken(storedToken) : null;
const initialState: AuthState = {
  user: initialUser,
  isAuthenticated: Boolean(initialUser),
  isLoading: false,
  error: null,
};

// 创建 slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        const tokenPayload = decodeToken(action.payload.token);
        state.user = tokenPayload;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // setUserFromToken
      .addCase(setUserFromToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setUserFromToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
      })
      .addCase(setUserFromToken.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
      })

      // logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.isLoading = false;
      });
  },
});

export default authSlice.reducer;
